import { OneBroker } from "@dune/types";
import { Button, Card, Separator } from "@dune/ui";
import React from "react";
import { FormProvider } from "react-hook-form";
import { useUpdateBrokerDetails } from "../../../../../api/mutations/societies.mutations";
import { useRequiredParams } from "../../../../../hooks/useRequiredParams";
import { useQueryClient } from "@tanstack/react-query";
import { societiesQueryKeys } from "../../../../../api/query-keys/societies.query-keys";
import { BrokerDetails } from "./apporteurTabFiles/BrokerDetails";
import { GraphicCharter } from "./apporteurTabFiles/GraphicCharter";
import { MainDetails } from "./apporteurTabFiles/MainDetails";
import { OriasDetails } from "./apporteurTabFiles/OriasDetails";
import { useEditBrokerDetailsForm } from "./apporteurTabFiles/useEditBrokerDetailsForm";

type Props = {
	broker: OneBroker;
};

const BrokerTab: React.FC<Props> = ({ broker }) => {
	const { id } = useRequiredParams<{ id: string }>();
	const brokerId = Number.parseInt(id);
	const { form } = useEditBrokerDetailsForm(broker);
	const { mutate, isPending } = useUpdateBrokerDetails();
	const queryClient = useQueryClient();

	const onSubmit = form.handleSubmit((data) => {
		mutate(
			{
				id: brokerId,
				data: {
					siren: data.siren,
					denomination: data.denomination,
					headquartersAddress: data.headquartersAddress,
					entityList: data.entityList,
					attachedToANetwork: data.attachedToANetwork,
					networkName: data.networkName,
					attachedToAGroup: data.attachedToAGroup,
					sirenGroup: data.sirenGroup,
					companyGroupName:
						data.attachedToAGroup === "true" ? data.companyGroupName : "",
					canGenerateApiKeys: data.canGenerateApiKeys,
					graphicCharter: {
						...(data.graphicCharter.logo
							? {
									logo: {
										name: data.graphicCharter.logo.name,
										bucket: data.graphicCharter.logo.bucket,
										key: data.graphicCharter.logo.key,
										mimeType: data.graphicCharter.logo.type,
										url: data.graphicCharter.logo.url || "",
									},
								}
							: {}),
						color: data.graphicCharter.color,
					},
				},
			},
			{
				onSuccess: async () => {
					await queryClient.invalidateQueries({
						queryKey: societiesQueryKeys.broker(brokerId),
					});
					window.location.reload();
				},
			},
		);
	});

	return (
		<FormProvider {...form}>
			<MainDetails
				brokerSocietyId={brokerId}
				canGenerateApiKeys={broker.canGenerateApiKeys ?? false}
			/>
			<Separator />
			<GraphicCharter />
			<Separator />
			<BrokerDetails />
			<Separator />
			<OriasDetails />

			<div className="fixed bottom-8 right-8 z-50">
				<Card className="bg-white shadow-lg p-4">
					<Button
						onClick={onSubmit}
						variant="default"
						className="w-full min-w-[200px]"
						disabled={isPending}
					>
						{isPending ? "Enregistrement..." : "Valider les modifications"}
					</Button>
				</Card>
			</div>
		</FormProvider>
	);
};

export default BrokerTab;
